import CustomField from "components/common/CustomField";
import CustomLabel from "components/common/CustomLabel";
import DetailsPageTitle from "components/common/DetailsPageTitle";
import MultipleSelect from "components/common/MultipleSelect";
import SingleSelect from "components/common/SingleSelect";
import Spinners from "components/common/Spinners";
import TimePicker from "components/common/TimePicker";
import {
  defaultEndTime,
  defaultStartTime,
} from "components/constants/Constants";
import { ErrorMessage, Form, Formik } from "formik";
import { CircleX } from "lucide-react";
import { Calendar } from "primereact/calendar";
import { InputSwitch } from "primereact/inputswitch";
import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getBranchList } from "store/actions/branchSlice";
import CalenderIcon from "assets/images/icons/calendarPrimary.svg";
import { formatDate } from "components/common/Utils";
import FileUpload from "components/common/FileUpload";
import { useNavigate, useSearchParams } from "react-router";
import FormButtons from "components/common/FormButtons";
import { addOfferSchema } from "components/common/Validation";
import { showAlert } from "store/reducers/alertReducer";
import {
  addOffer,
  getOfferDetails,
  updateOffer,
} from "store/actions/offerSlice";
import { format } from "date-fns";
import { getSpaceListByType } from "store/actions/workspaceSlice";
import {
  couponRoleTypes,
  spaceTypeOptions,
} from "components/constants/DropdownOptions";
import { MultiSelect } from "primereact/multiselect";
import { getCustomerDropdownList } from "store/actions/customerSlice";

const initialValues = {
  name: "",
  nameAr: "",
  discountType: "percentage",
  amount: null,
  applicableBranches: [],
  spaceType: "",
  spaces: [],
};

const AddOffer = ({ onClose, onSuccess, mode, offer, type }) => {
  const { t } = useTranslation();
  document.title =
    mode === "add"
      ? t("Add new offer | Da Nonna")
      : t("Edit offer | Da Nonna");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const id = offer?.value;

  const { isLoading, loading, offerDetails } = useSelector(
    (state) => state.Offers
  );
  const { branchesList } = useSelector((state) => state.Branches);
  const { spaceLists } = useSelector((state) => state.Workspace);
  const [formValues, setFormValues] = useState(initialValues);

  useEffect(() => {
    const fetchBranchesList = () => {
      dispatch(getBranchList());
    };
    fetchBranchesList();

    const getDetails = () => {
      const payload = {
        id,
      };
      dispatch(getOfferDetails(payload));
    };
    dispatch(getCustomerDropdownList({ keyword: "" }));

    mode === "edit" && getDetails();
  }, []);

  const fetchSpaceListByType = (spaceType, branches) => {
    const payload = {
      spaceTypes: [spaceType],
      branches: branches ?? [],
    };
    dispatch(getSpaceListByType(payload));
  };

  useEffect(() => {
    if (mode === "edit" && offerDetails) {
      const {
        name,
        nameAr,
        discountType,
        amount,
        applicableBranches,
        spaceType,
        spaces,
      } = offerDetails || {};

      fetchSpaceListByType(spaceType, applicableBranches);
      // Update formValues using destructured values
      setFormValues((prevValues) => ({
        ...prevValues,
        name: name,
        nameAr: nameAr,
        discountType,
        amount,
        applicableBranches,
        spaceType,
        spaces,
      }));
    }
  }, [mode, offerDetails]);

  const handleOnSubmit = (values) => {
    const {
      name,
      nameAr,
      discountType,
      amount,
      applicableBranches,
      spaceType,
      spaces,
    } = values;

    let payload = {
      name,
      nameAr,
      discountType,
      amount,
      applicableBranches: applicableBranches || [],
      spaceType: spaceType || "",
      spaces: spaces || [],
    };

    if (mode === "add") {
      dispatch(addOffer(payload))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            onClose(false);
            onSuccess();
            //navigate(-1);
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    } else if (mode === "edit") {
      payload.id = id;

      dispatch(updateOffer(payload))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            onClose(false);
            onSuccess();
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    }
  };

  const roomItemTemplate = (option) => {
    return (
      <div
        className="d-flex align-items-center gap-3 w-100"
        style={{
          border: "1px solid #D9D9D9",
          borderRadius: 10,
          padding: " 7px 10px",
        }}
      >
        <img
          src={option?.imageUrls[0]}
          // alt={option?.name}
          width={59}
          height={55}
          style={{ borderRadius: 10 }}
        />
        <div>
          <h2 className="fs_12 text_black">{option.name}</h2>

          <h2 className="fs_12 text_gray mt-1">
            <span>
              {option.cityId?.name}, {""}
            </span>
            <span>{option.branchId?.name}</span>
          </h2>
        </div>
      </div>
    );
  };

  const offerSpaceTypeOptions = [
    { name: "Workspace", value: "workSpace", icon: "pi pi-home" },
    {
      name: "Meeting Room",
      value: "meetingRoom",
      icon: "pi pi-home",
    },
  ];
  const getFilteredOptions = (type) => {
    if (type === "office") {
      return offerSpaceTypeOptions; // both
    }
    if (type === "meetingRoom") {
      return offerSpaceTypeOptions.filter((opt) => opt.value === "workSpace");
    }
    if (type === "workSpace") {
      return offerSpaceTypeOptions.filter((opt) => opt.value === "meetingRoom");
    }
    return []; // default empty if no match
  };

  return (
    <React.Fragment>
      <div className="page-content common_page add_workshop_page">
        {loading && mode === "edit" ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <Formik
                initialValues={formValues}
                validationSchema={addOfferSchema}
                enableReinitialize={true}
                onSubmit={handleOnSubmit}
              >
                {({ values, setFieldValue, errors, touched }) => (
                  <Form>
                    <div className="common_wrapper">
                      <h3 className="mb-4">
                        {mode === "edit"
                          ? t("Edit Offer For Plan")
                          : t("Add New Offer For Plan")}
                      </h3>
                      <Row>
                        <Col lg={6}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="name"
                              label={t("Plan Offer Name")}
                            />
                            <CustomField
                              name="name"
                              type="text"
                              placeholder={t("Name")}
                              touched={touched}
                              errors={errors}
                            />
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="nameAr"
                              label={t("Arabic Plan Offer Name")}
                            />
                            <CustomField
                              name="nameAr"
                              type="text"
                              placeholder={t("Enter Arabic Name")}
                              touched={touched}
                              errors={errors}
                              isArabic={true}
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row className="mt-2">
                        <Col lg={12}>
                          <div className="bg_white br_14 p_23_30 border_eee">
                            <h2 className="fs_20 mb-4">{t("Targeting")}</h2>
                            <Row>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomLabel
                                    htmlFor="applicableBranches"
                                    label={t("Branches")}
                                  />
                                  <MultipleSelect
                                    value={values.applicableBranches}
                                    handleMultipleSelectChange={(e) => {
                                      setFieldValue("applicableBranches", e);
                                      setFieldValue("spaceType", "");
                                    }}
                                    options={branchesList?.map((item) => ({
                                      name: item?.name,
                                      value: item?._id,
                                    }))}
                                    optionLabel="applicableBranches"
                                    placeholder={"Branches"}
                                    maxSelectedLabels={3}
                                    className={`${
                                      touched.applicableBranches &&
                                      errors.applicableBranches
                                        ? "invalid"
                                        : ""
                                    }`}
                                  />
                                  <ErrorMessage
                                    name={"applicableBranches"}
                                    component="div"
                                    className="text-danger"
                                  />
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomLabel
                                    htmlFor="spaceType"
                                    label={t("Space Types")}
                                  />

                                  <SingleSelect
                                    options={getFilteredOptions(type)}
                                    value={values?.spaceType}
                                    placeholder={t("Select Space Type")}
                                    handleSingleSelectChange={(e) => {
                                      setFieldValue(
                                        "spaceType",
                                        e.target.value
                                      );
                                      setFieldValue("spaces", []);
                                      fetchSpaceListByType(
                                        e.target.value,
                                        values.applicableBranches
                                      );
                                    }}
                                    className={`${
                                      errors?.spaceType && touched?.spaceType
                                        ? "invalid"
                                        : ""
                                    }`}
                                    error={errors?.spaceType}
                                  />
                                  <ErrorMessage
                                    name={"spaceTypes"}
                                    component="div"
                                    className="text-danger"
                                  />
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomLabel
                                    htmlFor="spaces"
                                    label={t("Products")}
                                  />
                                  <MultiSelect
                                    value={values.spaces}
                                    onChange={(e) =>
                                      setFieldValue("spaces", e.value)
                                    }
                                    options={(spaceLists || []).map((item) => ({
                                      name: item?.name,
                                      value: item?._id,
                                      cityId: item?.cityId,
                                      branchId: item?.branchId,
                                      imageUrls: item?.imageUrls,
                                    }))}
                                    optionLabel="name"
                                    display="chip"
                                    itemTemplate={roomItemTemplate}
                                    placeholder={"Products"}
                                    selectAllLabel={t("Select All")}
                                    className={`w-100 custom_multi_select coupon_product_select ${
                                      touched.spaces && errors.spaces
                                        ? "invalid"
                                        : ""
                                    }`}
                                    filter
                                    filterPlaceholder={t("Search")}
                                    panelClassName="coupon_product_select"
                                  />

                                  <ErrorMessage
                                    name={"spaces"}
                                    component="div"
                                    className="text-danger"
                                  />
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>

                      <Row className="mt-4">
                        <Col lg={12}>
                          <div className="bg_white br_14 p_23_30 border_eee">
                            <h2 className="fs_20 mb-4">
                              {t("Basic Information")}
                            </h2>
                            <Row>
                              <Col lg={12}>
                                <div className="mb-3">
                                  <div className="d-flex gap-3">
                                    <label className="custom-radio">
                                      <input
                                        type="radio"
                                        id="fixed"
                                        name="discountType"
                                        value="fixed"
                                        checked={
                                          values.discountType === "fixed"
                                        }
                                        onChange={() =>
                                          setFieldValue("discountType", "fixed")
                                        }
                                      />
                                      <span className="checkmark"></span>
                                      {t("Fixed Amount Discount")}
                                    </label>
                                    <label className="custom-radio">
                                      <input
                                        type="radio"
                                        id="percentage"
                                        name="discountType"
                                        value="percentage"
                                        checked={
                                          values.discountType === "percentage"
                                        }
                                        onChange={() =>
                                          setFieldValue(
                                            "discountType",
                                            "percentage"
                                          )
                                        }
                                      />
                                      <span className="checkmark"></span>
                                      {t("Percentage Discount")}
                                    </label>
                                    {values?.spaceType == "meetingRoom" && (
                                      <label className="custom-radio">
                                        <input
                                          type="radio"
                                          id="hourly"
                                          name="discountType"
                                          value="hourly"
                                          checked={
                                            values.discountType === "hourly"
                                          }
                                          onChange={() =>
                                            setFieldValue(
                                              "discountType",
                                              "hourly"
                                            )
                                          }
                                        />
                                        <span className="checkmark"></span>
                                        {t("Hourly Discount")}
                                      </label>
                                    )}
                                  </div>
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomLabel
                                    htmlFor="amount"
                                    label={t("Discount Value")}
                                  />
                                  <CustomField
                                    name="amount"
                                    type="number"
                                    placeholder={t("Discount Value")}
                                    touched={touched}
                                    errors={errors}
                                  />
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>

                      <div className="mt-2">
                        <Row className="mt-2">
                          <Col lg={12}>
                            <div className={`g_end gap-3 px-0 mt-4`}>
                              <button
                                className="cancel_btn"
                                type="button"
                                onClick={() => {
                                  onClose(false);
                                }}
                              >
                                {t("Cancel")}
                              </button>

                              <button
                                id="submitBtn"
                                className="main_btn"
                                type="submit"
                                disabled={isLoading}
                              >
                                {t("Save")}
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </Row>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default AddOffer;
